import React, { useState } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import { ReportData, TankSummary } from '../types/interfaces';
import { fetchMonthlyReport, fetchDailyReport } from '../utils/fetchUtils';
import { exportTankSummaryToExcel } from '../utils/excelUtils';
import { apiFetch } from '../utils/api';
import TtbBrewersReport from './TtbBrewersReport';
import ReportsHub from './ReportsHub';
import AbcBeerReport from './AbcBeerReport';
import { centralToday, centralMonth } from '../utils/centralDate';

const LegacyReports: React.FC<{ exportToExcel: (report: ReportData) => void }> = ({ exportToExcel }) => {
  const [report, setReport] = useState<ReportData | null>(null);
  const [reportMonth, setReportMonth] = useState(centralMonth());
  const [reportDate, setReportDate] = useState(centralToday());

  const handleFetchMonthlyReport = async () => {
    try {
      const data = await fetchMonthlyReport(reportMonth);
      setReport(data);
    } catch (err) {
      console.error('Report error:', err);
    }
  };

  const handleFetchDailyReport = async () => {
    try {
      const data = await fetchDailyReport(reportDate);
      setReport(data);
    } catch (err) {
      console.error('Daily report error:', err);
    }
  };

  const handlePhysicalInventory = async () => {
    try {
      const response = await apiFetch('/api/physical-inventory', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      alert(`Physical inventory recorded at ${data.timestamp}`);
    } catch (err: unknown) {
      console.error('Physical inventory error:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert('Failed to record physical inventory: ' + message);
    }
  };

  const sampleTankSummary: TankSummary = {
    barrelId: 'GNS250329',
    type: 'Spirits',
    proofGallons: '0',
    proof: '0',
    totalProofGallonsLeft: '0',
    date: 'N/A',
    fromAccount: 'Storage',
    toAccount: 'Processing',
    serialNumber: '250307',
    producingDSP: 'DSP-KY-417',
  };

  return (
    <div className="no-print" data-testid="legacy-reports">
      <p>
        <Link to="/reporting">All reports</Link>
      </p>
      <h2 className="app-header">Older summaries</h2>
      <div>
        <input type="month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} />
        <button onClick={handleFetchMonthlyReport}>Fetch Monthly Report</button>
        {report && report.month && <button onClick={() => exportToExcel(report)}>Export to Excel</button>}
      </div>
      <div>
        <input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
        <button onClick={handleFetchDailyReport}>Fetch Daily Report</button>
      </div>
      <div>
        <button onClick={handlePhysicalInventory}>Record Physical Inventory</button>
        <button onClick={() => exportTankSummaryToExcel(sampleTankSummary)}>Export Tank Summary</button>
      </div>
      {report ? (
        <div>
          <h3>{report.month ? `Monthly Report: ${report.month}` : `Daily Report: ${report.date}`}</h3>
          <p>Total Received: {Number(report.totalReceived).toFixed(2)} PG</p>
          <p>Total Processed: {Number(report.totalProcessed).toFixed(2)} PG</p>
          {report.totalMoved !== undefined && <p>Total Moved: {Number(report.totalMoved).toFixed(2)} PG</p>}
          {report.totalRemoved !== undefined && (
            <p>Total Removed: {Number(report.totalRemoved).toFixed(2)} PG</p>
          )}
          {report.byType && (
            <div>
              <h4>Processed by Type</h4>
              <ul>
                {Object.entries(report.byType).map(([type, pg]) => (
                  <li key={type}>{`${type}: ${Number(pg).toFixed(2)} PG`}</li>
                ))}
              </ul>
            </div>
          )}
          {report.transactions && (
            <ul>
              {report.transactions.map((t, i) => (
                <li key={i}>
                  {`${t.action}: ${Number(t.proofGallons).toFixed(2)} PG (${t.type}) on ${t.date} ${
                    t.barrelId ? `(Barrel: ${t.barrelId})` : ''
                  } ${t.toAccount ? `to ${t.toAccount}` : ''}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <p>Select a month or date and fetch a report.</p>
      )}
    </div>
  );
};

const Reporting: React.FC<{ exportToExcel: (report: ReportData) => void }> = ({ exportToExcel }) => {
  return (
    <div className="page-container">
      <Routes>
        <Route index element={<ReportsHub />} />
        <Route path="ttb" element={<TtbBrewersReport />} />
        <Route path="abc/:reportId" element={<AbcBeerReport />} />
        <Route path="legacy" element={<LegacyReports exportToExcel={exportToExcel} />} />
      </Routes>
    </div>
  );
};

export default Reporting;
